import { useMemo, useState } from "react"
import { Link } from "react-router-dom"
import {
  AtSign, Briefcase, Calendar, CheckSquare, ExternalLink, FileText, GitBranch,
  History, Mail, MapPin, Phone, PhoneIncoming, PhoneOutgoing, Paperclip,
  TriangleAlert, User, Zap,
} from "lucide-react"
import { parseServerDate } from "@/lib/utils"
import { ohneIds } from "@/lib/ohneIds"
import { EmailModal } from "@/components/portal/EmailModal"
import { InhaltEntferntHinweis } from "@/components/portal/InhaltEntfernt"
import { CalendarEditModal } from "@/components/portal/CalendarEditModal"
import { TaskEditModal } from "@/components/portal/TaskEditModal"
import { zurueckLink } from "./ZurueckZurSuche"
import {
  GRAPH_ART_NAMEN, GRAPH_FARBEN, KindIcon, RelationGraph,
  type Graph, type GraphNode,
} from "./RelationGraph"

/**
 * Die Vollansicht zu einer Person, einem Vorgang oder einer Rufnummer.
 *
 * Anlass ist das laufende Telefonat: was hier steht, muss ohne Scrollen und
 * ohne Nachdenken beantworten, mit wem man spricht und was mit dem laeuft.
 * Darum oben die Identitaet samt Zahlen, dann der Graph als Bild, und darunter
 * jede Liste einzeln — nichts ist nur im Graph sichtbar.
 *
 * Wird an drei Stellen verwendet: auf der Suchseite, in der globalen Suche und
 * aus dem Anruf-Banner heraus.
 */

export type DossierDaten = {
  focus: {
    contact_id: number | null
    project_id: number | null
    phone: string | null
    phone_norm: string | null
    tenant_slug: string | null
    unknown_number: boolean
  }
  contact: null | {
    id: number; nr: string | null; name: string; company_name: string
    email: string; phone_mobile: string; phone_home: string
    street: string; postcode: string; city: string; tenant_slug: string
    /** true = nicht aus dem Cache, sondern live aus HERO nachgeladen. */
    aus_hero?: boolean
    /** true = in HERO geloescht. Der Vorgang zeigt trotzdem noch darauf. */
    geloescht?: boolean
  }
  contact_persons: Array<{
    id: number; nr: string | null; name: string; email: string; phone: string
    position: string
    /** true = das ist der Kunde ueber diesem Ansprechpartner, nicht daneben. */
    ist_kunde: boolean
  }>
  projects: Array<{
    id: number; project_nr: string | null; title: string; label: string
    status: string; contact_name: string; address: string; tenant_slug: string
    /** Nur gesetzt, wenn der Kunde ein ANDERER ist als der Ansprechpartner. */
    customer_id: number | null
    customer_name: string
  }>
  emails: Array<{
    id: number; title: string; sender: string; sender_email: string
    direction: string; status: string; category: string
    has_attachments: boolean; date: string | null; project_id: number | null
    /** Vorgang nicht gesetzt, sondern aus dem Verlauf abgeleitet. */
    project_via_thread?: boolean
    project_via_plan?: string | null
    thread_id?: string | null
    /** Inhalt vom Datenschutz-Löschjob geleert (nach 90 Tagen). */
    inhalt_entfernt?: boolean
    message_id?: string | null
    imap_folder?: string | null
    postfach?: string | null
    postfach_verfuegbar?: boolean
    tenant_slug?: string | null
    /** gesendet | keine_antwort | null — ob auf diese Mail eine Antwort rausging.
     *  „keine_antwort" = der Plan schlug eine vor, sie wurde abgewählt, bis jetzt
     *  ging nichts raus (Kunde 1629, 20.10.2026). */
    antwort_status?: "gesendet" | "keine_antwort" | null
    antwort_at?: string | null
    plan_ausgang?: string | null
    plan_ausgang_benutzer?: string | null
  }>
  /** Was das Portal mit dieser Person GETAN hat: Aktivitätslog (30 Tage) und
   *  HERO-Logbuch-Vermerke. Ohne das zeigt das Dossier nur, was es gibt. */
  actions?: Array<{
    kind: "action"; id: string; action: string; label: string; detail: string
    benutzer: string | null; source: string; status: string | null
    date: string | null; email_id: number | null; project_id: number | null
    /** Abgewählte Plan-Schritte, z.B. „Antwort senden". */
    abgewaehlt: string[]
  }>
  calls: Array<{
    id: number; call_id: string; title: string; subtitle: string; summary: string
    direction: string; status: string; number: string | null
    duration_secs: number | null; date: string | null; project_id: number | null
  }>
  documents: Array<{
    id: number; title: string; type_name: string; status: string
    amount: number | null; date: string | null; link_view: string | null
    project_id: number | null; project_nr: string | null
  }>
  tasks: Array<{
    id: number; hero_task_id: number | null; title: string; comment: string
    status: string; assignee: string; assignee_id: number | null
    date: string | null; project_id: number | null
  }>
  appointments: Array<{
    id: number; title: string; date: string | null; end: string | null
    all_day: boolean; is_done: boolean; project_id: number | null
    category_id: number | null
  }>
  stats: Record<string, number>
  graph: Graph
}

/**
 * Neueste zuerst — fuer jede Liste im Dossier dieselbe Regel.
 *
 * „Hier wäre gut, wenn alles immer chronologisch sortiert wäre" (Marlene,
 * 02.09.2026). In der Aufgaben-Liste standen vier erledigte vor zwei offenen,
 * ohne erkennbare Ordnung und ohne Datum an der Zeile — wer wissen wollte,
 * was zuletzt passiert ist, musste raten.
 *
 * 🔑 Eintraege OHNE Datum wandern nach unten statt nach oben. Sie sind sonst
 * das Erste, was man sieht, und sagen am wenigsten. Sortiert wird auf einer
 * Kopie: die Reihenfolge, in der der Server liefert, gehoert ihm.
 */
function nachDatum<T extends { date?: string | null }>(liste: readonly T[]): T[] {
  return [...liste].sort((a, b) => {
    const da = a.date || ""
    const db = b.date || ""
    if (!da && !db) return 0
    if (!da) return 1
    if (!db) return -1
    return db.localeCompare(da)
  })
}

const HERO_WEB_BASE = "https://login.hero-software.de"
function heroLink(linkView: string | null): string | null {
  if (!linkView) return null
  if (/^https?:\/\//.test(linkView)) return linkView
  return HERO_WEB_BASE + (linkView.startsWith("/") ? "" : "/") + linkView
}

function datum(iso: string | null | undefined, mitZeit = true): string {
  const d = parseServerDate(iso)
  if (!d) return "—"
  return d.toLocaleString("de-DE", {
    day: "2-digit", month: "2-digit", year: "2-digit",
    ...(mitZeit ? { hour: "2-digit", minute: "2-digit" } : {}),
  })
}

function dauer(secs: number | null): string {
  if (!secs || secs <= 0) return ""
  const m = Math.floor(secs / 60)
  return m > 0 ? `${m}:${String(secs % 60).padStart(2, "0")} min` : `${secs}s`
}

function euro(n: number | null): string {
  if (n == null) return ""
  return n.toLocaleString("de-DE", { style: "currency", currency: "EUR" })
}

// ─────────────────────────────────────────────────────────────────────────────

function Kachel({ label, wert, farbe, aktiv, onClick }: {
  label: string; wert: number | string; farbe: string
  aktiv?: boolean; onClick?: () => void
}) {
  return (
    <button
      onClick={onClick}
      style={{
        background: aktiv ? farbe : "#fff",
        border: `1px solid ${aktiv ? farbe : "#e5e7eb"}`,
        borderRadius: 10, padding: "9px 13px", cursor: onClick ? "pointer" : "default",
        textAlign: "left", minWidth: 82, transition: "background 120ms",
      }}
    >
      <div style={{
        fontSize: 19, fontWeight: 700, lineHeight: 1.1,
        color: aktiv ? "#fff" : farbe,
      }}>
        {wert}
      </div>
      <div style={{
        fontSize: 12, fontWeight: 600, textTransform: "uppercase",
        letterSpacing: 0.3, color: aktiv ? "rgba(255,255,255,0.85)" : "#94a3b8",
        marginTop: 2,
      }}>
        {label}
      </div>
    </button>
  )
}

function Zeile({ icon, titel, unten, rechts, href, extern, onClick }: {
  icon: React.ReactNode; titel: React.ReactNode; unten?: React.ReactNode
  rechts?: React.ReactNode; href?: string; extern?: boolean; onClick?: () => void
}) {
  const inhalt = (
    <>
      <div style={{ flexShrink: 0, marginTop: 1 }}>{icon}</div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div data-pii style={{
          fontSize: 13, fontWeight: 600, color: "#0f172a",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {titel}
        </div>
        {unten && (
          <div data-pii style={{
            fontSize: 12, color: "#64748b", marginTop: 1,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {unten}
          </div>
        )}
      </div>
      {rechts && (
        <div style={{ flexShrink: 0, fontSize: 12, color: "#94a3b8", textAlign: "right" }}>
          {rechts}
        </div>
      )}
      {extern && <ExternalLink style={{ width: 12, height: 12, color: "#94a3b8", flexShrink: 0 }} />}
    </>
  )
  const stil: React.CSSProperties = {
    display: "flex", alignItems: "flex-start", gap: 10, padding: "9px 12px",
    borderBottom: "1px solid #f1f5f9", textDecoration: "none",
    cursor: href || onClick ? "pointer" : "default",
  }
  if (extern && href) {
    return <a href={href} target="_blank" rel="noreferrer" style={stil}>{inhalt}</a>
  }
  if (href) return <Link to={href} style={stil}>{inhalt}</Link>
  return <div style={stil} onClick={onClick}>{inhalt}</div>
}

/** „Im Postfach öffnen“ für eine geleerte Mail (Titel trägt den Hinweis schon). */
/** „beantwortet 19.10." oder deutlich „keine Antwort gesendet" — sonst nichts.
 *  Das Dossier behauptet nur, was es weiß: ohne Plan oder Antwortvorschlag
 *  steht hier kein Urteil. */
function antwortBadge(e: DossierDaten["emails"][number]) {
  if (e.antwort_status === "keine_antwort") {
    return (
      <span data-testid="antwort-fehlt" style={{
        marginLeft: 6, fontSize: 12, fontWeight: 600,
        color: "#b91c1c", background: "#fef2f2",
        border: "1px solid #fecaca", borderRadius: 4, padding: "0 5px",
      }}>
        keine Antwort gesendet
      </span>
    )
  }
  if (e.antwort_status === "gesendet") {
    return (
      <span data-testid="antwort-gesendet" style={{
        marginLeft: 6, fontSize: 12, fontWeight: 600,
        color: "#15803d", background: "#f0fdf4",
        border: "1px solid #bbf7d0", borderRadius: 4, padding: "0 5px",
      }}>
        beantwortet{e.antwort_at ? ` ${datum(e.antwort_at, false)}` : ""}
      </span>
    )
  }
  return null
}

function postfachKnopf(e: DossierDaten["emails"][number], tenant: string) {
  return (
    <InhaltEntferntHinweis
      kompakt
      tenant={e.tenant_slug || tenant}
      messageId={e.message_id}
      folder={e.imap_folder}
      postfach={e.postfach}
      postfachVerfuegbar={e.postfach_verfuegbar}
    />
  )
}

function Liste({ children, leer }: { children: React.ReactNode; leer: string }) {
  const hat = Array.isArray(children) ? children.length > 0 : !!children
  if (!hat) {
    return <div style={{ padding: "26px 12px", textAlign: "center", color: "#94a3b8", fontSize: 12.5 }}>{leer}</div>
  }
  return <div>{children}</div>
}

// ─────────────────────────────────────────────────────────────────────────────

type Reiter = "graph" | "verlauf" | "emails" | "calls" | "documents" | "appointments" | "tasks" | "projects"

export function Dossier({
  daten,
  kompakt = false,
  onOeffneKontakt,
}: {
  daten: DossierDaten
  /** Im Popover: weniger Höhe, kein Verlauf-Reiter als Standard. */
  kompakt?: boolean
  onOeffneKontakt?: (contactId: number) => void
}) {
  const [reiter, setReiter] = useState<Reiter>("graph")
  // Popups statt Seitenwechsel: aus der Suche heraus ist ein Eintrag ein Blick,
  // kein Ortswechsel. Wer waehrend eines Telefonats nachschaut, will danach
  // wieder in seiner Trefferliste stehen.
  const [mailOffen, setMailOffen] = useState<number | null>(null)
  const [terminOffen, setTerminOffen] = useState<DossierDaten["appointments"][number] | null>(null)
  const [aufgabeOffen, setAufgabeOffen] = useState<DossierDaten["tasks"][number] | null>(null)
  const k = daten.contact
  const s = daten.stats || {}
  const tenant = daten.focus.tenant_slug || k?.tenant_slug || ""

  // Ein gemeinsamer, chronologischer Verlauf. Waehrend eines Gesprächs ist
  // „was war zuletzt?" die haeufigste Frage — und die Antwort steckt sonst
  // verteilt in fuenf Listen.
  const verlauf = useMemo(() => {
    type Eintrag = { art: Reiter; ts: number; el: React.ReactNode }
    const alle: Eintrag[] = []
    const ts = (iso: string | null | undefined) => parseServerDate(iso)?.getTime() ?? 0

    for (const e of daten.emails) {
      alle.push({ art: "emails", ts: ts(e.date), el: (
        <Zeile
          key={`m${e.id}`}
          icon={<KindIcon kind="email" />}
          titel={e.title}
          unten={<>
            {`${e.direction === "sent" ? "An" : "Von"} ${e.sender || e.sender_email || "—"}`}
            {antwortBadge(e)}
            {e.inhalt_entfernt && <> {postfachKnopf(e, tenant)}</>}
          </>}
          rechts={datum(e.date)}
          onClick={() => setMailOffen(e.id)}
        />
      ) })
    }
    // Die Bearbeitungsschritte — „was wurde damit gemacht?" (Paul, 20.10.2026).
    // Kontakt/Vorgang/Termin angelegt, Plan-Ausgang samt abgewählten Schritten,
    // HERO-Logbuch. Ohne diese Zeilen zeigt der Verlauf nur, was existiert.
    for (const a of daten.actions ?? []) {
      const abgewaehlt = a.abgewaehlt?.length ? `abgewählt: ${a.abgewaehlt.join(", ")}` : ""
      alle.push({ art: "verlauf", ts: ts(a.date), el: (
        <Zeile
          key={a.id}
          icon={<Zap style={{ width: 13, height: 13, color: a.status === "error" || abgewaehlt ? "#b91c1c" : "#64748b" }} />}
          titel={ohneIds(a.label)}
          unten={[
            a.detail && ohneIds(a.detail) !== ohneIds(a.label) ? ohneIds(a.detail) : "",
            abgewaehlt,
            a.benutzer || (a.source === "hero" ? "HERO" : ""),
          ].filter(Boolean).join(" · ")}
          rechts={datum(a.date)}
          onClick={a.email_id ? () => setMailOffen(a.email_id!) : undefined}
        />
      ) })
    }
    for (const c of daten.calls) {
      alle.push({ art: "calls", ts: ts(c.date), el: (
        <Zeile
          key={`c${c.id}`}
          icon={c.direction === "in"
            ? <PhoneIncoming style={{ width: 13, height: 13, color: GRAPH_FARBEN.call }} />
            : <PhoneOutgoing style={{ width: 13, height: 13, color: GRAPH_FARBEN.call }} />}
          titel={c.subtitle || c.title}
          unten={[c.number, dauer(c.duration_secs), c.status === "missed" ? "verpasst" : ""]
            .filter(Boolean).join(" · ")}
          rechts={datum(c.date)}
          href={`/sipgate/${c.id}?${zurueckLink()}`}
        />
      ) })
    }
    for (const d of daten.documents) {
      alle.push({ art: "documents", ts: ts(d.date), el: (
        <Zeile
          key={`d${d.id}`}
          icon={<KindIcon kind="document" />}
          titel={`${d.title}${d.type_name ? ` · ${d.type_name}` : ""}`}
          unten={[d.status, d.project_nr].filter(Boolean).join(" · ")}
          rechts={<>{euro(d.amount)}<br />{datum(d.date, false)}</>}
          href={heroLink(d.link_view) || undefined}
          extern={!!d.link_view}
        />
      ) })
    }
    for (const a of daten.appointments) {
      alle.push({ art: "appointments", ts: ts(a.date), el: (
        <Zeile
          key={`a${a.id}`}
          icon={<KindIcon kind="appointment" />}
          titel={a.title}
          unten={a.is_done ? "erledigt" : a.all_day ? "ganztägig" : ""}
          rechts={datum(a.date, !a.all_day)}
          onClick={() => setTerminOffen(a)}
        />
      ) })
    }
    for (const t of daten.tasks) {
      alle.push({ art: "tasks", ts: ts(t.date), el: (
        <Zeile
          key={`t${t.id}`}
          icon={<KindIcon kind="task" />}
          titel={t.title}
          unten={[t.assignee, t.status].filter(Boolean).join(" · ")}
          rechts={t.date ? datum(t.date, false) : ""}
          onClick={() => setAufgabeOffen(t)}
        />
      ) })
    }
    return alle.sort((a, b) => b.ts - a.ts)
  }, [daten, tenant])

  const reiterListe: Array<{ id: Reiter; label: string; n?: number; icon: React.ReactNode }> = [
    { id: "graph", label: "Zusammenhänge", icon: <GitBranch style={{ width: 13, height: 13 }} /> },
    { id: "verlauf", label: "Verlauf", n: verlauf.length, icon: <History style={{ width: 13, height: 13 }} /> },
    { id: "projects", label: "Vorgänge", n: daten.projects.length, icon: <Briefcase style={{ width: 13, height: 13 }} /> },
    { id: "emails", label: "E-Mails", n: s.emails ?? daten.emails.length, icon: <Mail style={{ width: 13, height: 13 }} /> },
    { id: "calls", label: "Anrufe", n: s.calls ?? daten.calls.length, icon: <Phone style={{ width: 13, height: 13 }} /> },
    { id: "documents", label: "Belege", n: daten.documents.length, icon: <FileText style={{ width: 13, height: 13 }} /> },
    { id: "appointments", label: "Termine", n: daten.appointments.length, icon: <Calendar style={{ width: 13, height: 13 }} /> },
    { id: "tasks", label: "Aufgaben", n: daten.tasks.length, icon: <CheckSquare style={{ width: 13, height: 13 }} /> },
  ]

  const graphKlick = (n: GraphNode) => {
    if (!n.ref_id) return
    if (n.kind === "email") setMailOffen(n.ref_id)
    else if (n.kind === "project") window.location.assign(`/projekte/${n.ref_id}?tenant=${encodeURIComponent(tenant)}`)
    else if (n.kind === "call") window.location.assign(`/sipgate/${n.ref_id}`)
    else if (n.kind === "document") {
      const d = daten.documents.find((x) => x.id === n.ref_id)
      const url = heroLink(d?.link_view || null)
      if (url) window.open(url, "_blank", "noreferrer")
    } else if (n.kind === "appointment") {
      const a = daten.appointments.find((x) => x.id === n.ref_id)
      if (a) setTerminOffen(a)
    } else if (n.kind === "task") {
      const t = daten.tasks.find((x) => x.id === n.ref_id)
      if (t) setAufgabeOffen(t)
    } else if (n.kind === "contact" && onOeffneKontakt) onOeffneKontakt(n.ref_id)
  }

  return (
    <div>
      {/* ── Kopf: wer ist das ── */}
      <div style={{
        background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12,
        padding: 16, marginBottom: 12,
      }}>
        {daten.focus.unknown_number ? (
          <div style={{ display: "flex", gap: 11, alignItems: "flex-start" }}>
            <div style={{
              width: 42, height: 42, borderRadius: "50%", background: "#fef3c7",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <TriangleAlert style={{ width: 20, height: 20, color: "#d97706" }} />
            </div>
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, color: "#0f172a" }} data-pii>
                {daten.focus.phone}
              </div>
              <div style={{ fontSize: 12.5, color: "#92400e", marginTop: 2 }}>
                Zu dieser Nummer ist kein Kontakt hinterlegt.
                {s.calls ? ` Bisher ${s.calls} Anruf${s.calls === 1 ? "" : "e"} von hier.` : ""}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 13, alignItems: "flex-start", flexWrap: "wrap" }}>
            <div style={{
              width: 46, height: 46, borderRadius: "50%", background: GRAPH_FARBEN.contact,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <User style={{ width: 22, height: 22, color: "#fff" }} />
            </div>
            <div style={{ minWidth: 200, flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 19, fontWeight: 700, color: "#0f172a" }} data-pii>
                  {k?.name || daten.projects[0]?.contact_name || "Ohne Namen"}
                </span>
                {k?.nr && (
                  <span style={{
                    fontSize: 12, fontWeight: 600, color: "#475569", background: "#f1f5f9",
                    border: "1px solid #e2e8f0", borderRadius: 5, padding: "1px 6px",
                  }}>
                    {k.nr}
                  </span>
                )}
              </div>
              {k?.company_name && (
                <div style={{ fontSize: 13, color: "#475569", marginTop: 1 }} data-pii>{k.company_name}</div>
              )}
              {/* 🔴 Ein geloeschter Kontakt sieht sonst aus wie ein normaler —
                  und „Kontakt anlegen" waere an ihm die falsche Aktion. */}
              {k?.geloescht && (
                <div style={{
                  display: "inline-flex", alignItems: "center", gap: 5, marginTop: 5,
                  fontSize: 12, fontWeight: 600, color: "#92400e",
                  background: "#fffbeb", border: "1px solid #fde68a",
                  borderRadius: 6, padding: "2px 7px",
                }}>
                  <TriangleAlert style={{ width: 12, height: 12 }} />
                  In HERO gelöscht — der Vorgang zeigt weiterhin auf ihn
                </div>
              )}
              <div data-pii style={{
                display: "flex", flexWrap: "wrap", gap: "4px 16px", marginTop: 7,
                fontSize: 12.5, color: "#334155",
              }}>
                {(k?.phone_mobile || k?.phone_home) && (
                  <a href={`tel:${k.phone_mobile || k.phone_home}`}
                     style={{ display: "inline-flex", alignItems: "center", gap: 5, color: "#334155", textDecoration: "none" }}>
                    <Phone style={{ width: 12, height: 12, color: "#94a3b8" }} />
                    {k.phone_mobile || k.phone_home}
                  </a>
                )}
                {k?.email && (
                  <a href={`mailto:${k.email}`}
                     style={{ display: "inline-flex", alignItems: "center", gap: 5, color: "#334155", textDecoration: "none" }}>
                    <AtSign style={{ width: 12, height: 12, color: "#94a3b8" }} />
                    {k.email}
                  </a>
                )}
                {(k?.street || k?.city) && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                    <MapPin style={{ width: 12, height: 12, color: "#94a3b8" }} />
                    {[k.street, `${k.postcode || ""} ${k.city || ""}`.trim()].filter(Boolean).join(", ")}
                  </span>
                )}
              </div>
            </div>
            {!!daten.contact_persons?.length && (
              <div style={{
                flexBasis: "100%", marginTop: 9, paddingTop: 9,
                borderTop: "1px dashed #e2e8f0",
              }}>
                <div style={{
                  fontSize: 12, fontWeight: 700, textTransform: "uppercase",
                  letterSpacing: 0.4, color: "#94a3b8", marginBottom: 5,
                }}>
                  {daten.contact_persons.some((a) => a.ist_kunde)
                    ? "Gehört zu · weitere Ansprechpartner"
                    : "Ansprechpartner"}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                  {daten.contact_persons.map((ap) => (
                    <button
                      key={ap.id}
                      onClick={() => onOeffneKontakt?.(ap.id)}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        fontSize: 12, color: "#334155", background: "#f8fafc",
                        border: "1px solid #e2e8f0", borderRadius: 8,
                        padding: "4px 9px", cursor: "pointer",
                      }}
                    >
                      <User style={{
                        width: 11, height: 11,
                        color: ap.ist_kunde ? "#0284c7" : "#94a3b8",
                      }} />
                      <span style={{ fontWeight: ap.ist_kunde ? 700 : 500 }} data-pii>{ap.name}</span>
                      {/* Die Rolle ist die eigentliche Auskunft — „Ansprechpartner
                          Objektverwaltung" sagt mehr als der Name. */}
                      {ap.position && (
                        <span style={{ color: "#64748b" }}>· {ap.position}</span>
                      )}
                      {ap.phone && <span style={{ color: "#94a3b8" }} data-pii>· {ap.phone}</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {k && (
              <div style={{ display: "flex", gap: 7, flexShrink: 0 }}>
                {/* Direkt in HERO — der Weg laeuft ueber den Proxy, der den
                    Mandanten-Token setzt und dann weiterleitet. */}
                <a
                  href={`/admin/open-hero/contact/${k.id}?tenant=${encodeURIComponent(tenant)}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 5,
                    fontSize: 12, fontWeight: 600, color: "#fff", background: "#0284c7",
                    border: "1px solid #0284c7", borderRadius: 8, padding: "6px 11px",
                    textDecoration: "none", whiteSpace: "nowrap",
                  }}
                >
                  In HERO öffnen
                  <ExternalLink style={{ width: 12, height: 12 }} />
                </a>
                <Link
                  to={`/contacts/${k.id}?${zurueckLink()}`}
                  style={{
                    fontSize: 12, fontWeight: 600, color: "#334155", background: "#f8fafc",
                    border: "1px solid #e2e8f0", borderRadius: 8, padding: "6px 11px",
                    textDecoration: "none", whiteSpace: "nowrap",
                  }}
                >
                  Im Portal
                </Link>
              </div>
            )}
          </div>
        )}

        {/* Zahlen — gleichzeitig Sprungmarken in die Listen */}
        <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
          <Kachel label="Vorgänge" wert={daten.projects.length} farbe={GRAPH_FARBEN.project}
                  aktiv={reiter === "projects"} onClick={() => setReiter("projects")} />
          <Kachel label="E-Mails" wert={s.emails ?? daten.emails.length} farbe={GRAPH_FARBEN.email}
                  aktiv={reiter === "emails"} onClick={() => setReiter("emails")} />
          <Kachel label="Anrufe" wert={s.calls ?? daten.calls.length} farbe={GRAPH_FARBEN.call}
                  aktiv={reiter === "calls"} onClick={() => setReiter("calls")} />
          <Kachel label="Belege" wert={daten.documents.length} farbe={GRAPH_FARBEN.document}
                  aktiv={reiter === "documents"} onClick={() => setReiter("documents")} />
          <Kachel label="Termine" wert={daten.appointments.length} farbe={GRAPH_FARBEN.appointment}
                  aktiv={reiter === "appointments"} onClick={() => setReiter("appointments")} />
          <Kachel label="Aufgaben" wert={daten.tasks.length} farbe={GRAPH_FARBEN.task}
                  aktiv={reiter === "tasks"} onClick={() => setReiter("tasks")} />
          {!!s.documents_sum && (
            <Kachel label="Belegsumme" wert={euro(s.documents_sum)} farbe="#475569" />
          )}
        </div>
      </div>

      {/* ── Reiter ── */}
      <div style={{
        display: "flex", gap: 3, marginBottom: 10, flexWrap: "wrap",
        borderBottom: "1px solid #e5e7eb", paddingBottom: 8,
      }}>
        {reiterListe.map((r) => (
          <button
            key={r.id}
            onClick={() => setReiter(r.id)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5,
              fontWeight: 600, padding: "6px 11px", borderRadius: 8, cursor: "pointer",
              border: "1px solid " + (reiter === r.id ? "#cbd5e1" : "transparent"),
              background: reiter === r.id ? "#f1f5f9" : "transparent",
              color: reiter === r.id ? "#0f172a" : "#64748b",
            }}
          >
            {r.icon}
            {r.label}
            {r.n != null && (
              <span style={{
                fontSize: 12, fontWeight: 700, background: reiter === r.id ? "#fff" : "#f1f5f9",
                borderRadius: 5, padding: "0 5px", color: "#475569",
              }}>
                {r.n}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Inhalt ── */}
      <div style={{
        background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12,
        overflow: "hidden",
      }}>
        {reiter === "graph" && (
          <div style={{ padding: 14 }}>
            <RelationGraph
              graph={daten.graph}
              height={kompakt ? 380 : 560}
              onSelect={graphKlick}
            />
          </div>
        )}

        {reiter === "verlauf" && (
          <Liste leer="Noch kein Verlauf zu dieser Person.">
            {verlauf.map((v) => v.el)}
          </Liste>
        )}

        {reiter === "projects" && (
          <Liste leer="Keine Vorgänge hinterlegt.">
            {daten.projects.map((p) => (
              <Zeile
                key={p.id}
                icon={<KindIcon kind="project" />}
                /* 🔴 HERO-Bezeichnung, nie die interne ID. */
                titel={p.label}
                unten={[
                  p.status,
                  // 🔴 Weicht der Kunde vom Ansprechpartner ab, muss BEIDES
                  // dastehen — sonst ruft man den Falschen an oder schickt die
                  // Rechnung an die falsche Adresse.
                  p.customer_name
                    ? `Kunde: ${p.customer_name} · Ansprechpartner: ${p.contact_name}`
                    : p.contact_name,
                  p.address,
                ].filter(Boolean).join(" · ")}
                rechts={GRAPH_ART_NAMEN.project}
                href={`/projekte/${p.id}?tenant=${encodeURIComponent(p.tenant_slug || tenant)}&${zurueckLink()}`}
              />
            ))}
          </Liste>
        )}

        {reiter === "emails" && (
          <Liste leer="Keine Korrespondenz gefunden.">
            {nachDatum(daten.emails).map((e) => (
              <Zeile
                key={e.id}
                icon={<KindIcon kind="email" />}
                titel={
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    {e.title}
                    {e.has_attachments && <Paperclip style={{ width: 11, height: 11, color: "#94a3b8" }} />}
                  </span>
                }
                unten={
                  <>
                    {[
                      e.direction === "sent" ? "gesendet" : "empfangen",
                      e.sender || e.sender_email,
                      e.category,
                    ].filter(Boolean).join(" · ")}
                    {antwortBadge(e)}
                    {/* Geleert nach 90 Tagen: Original live aus dem Postfach. */}
                    {e.inhalt_entfernt && <> {postfachKnopf(e, tenant)}</>}
                    {/* Eine abgeleitete Zuordnung darf nicht wie eine gesetzte
                        aussehen — sie steht so nicht in HERO. */}
                    {e.project_via_thread && (
                      <span style={{
                        marginLeft: 6, fontSize: 12, fontWeight: 600,
                        color: "#0369a1", background: "#f0f9ff",
                        border: "1px dashed #7dd3fc", borderRadius: 4,
                        padding: "0 5px",
                      }}>
                        Vorgang über den Verlauf
                      </span>
                    )}
                    {/* Schwächer als die Vererbung: die stammt von einer
                        Handzuordnung, das hier nur vom Planer. Deshalb steht
                        die Bezeichnung dabei — man soll es prüfen können. */}
                    {e.project_via_plan && (
                      <span style={{
                        marginLeft: 6, fontSize: 12, fontWeight: 600,
                        color: "#7c3aed", background: "#faf5ff",
                        border: "1px dashed #d8b4fe", borderRadius: 4,
                        padding: "0 5px",
                      }}>
                        Vorgang laut Plan: {e.project_via_plan}
                      </span>
                    )}
                  </>
                }
                rechts={datum(e.date)}
                onClick={() => setMailOffen(e.id)}
              />
            ))}
          </Liste>
        )}

        {reiter === "calls" && (
          <Liste leer="Keine Anrufe zu dieser Nummer.">
            {nachDatum(daten.calls).map((c) => (
              <div key={c.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                <Zeile
                  icon={c.direction === "in"
                    ? <PhoneIncoming style={{ width: 13, height: 13, color: GRAPH_FARBEN.call }} />
                    : <PhoneOutgoing style={{ width: 13, height: 13, color: GRAPH_FARBEN.call }} />}
                  titel={c.subtitle || c.title}
                  unten={[
                    c.direction === "in" ? "eingehend" : "ausgehend",
                    c.number, dauer(c.duration_secs),
                    c.status === "missed" ? "verpasst" : "",
                  ].filter(Boolean).join(" · ")}
                  rechts={datum(c.date)}
                  href={`/sipgate/${c.id}?${zurueckLink()}`}
                />
                {c.summary && (
                  <div style={{
                    fontSize: 12, color: "#475569", background: "#f8fafc",
                    padding: "8px 12px 10px 34px", lineHeight: 1.45,
                  }}>
                    {c.summary}
                  </div>
                )}
              </div>
            ))}
          </Liste>
        )}

        {reiter === "documents" && (
          <Liste leer="Keine Angebote oder Rechnungen gefunden.">
            {nachDatum(daten.documents).map((d) => (
              <Zeile
                key={d.id}
                icon={<KindIcon kind="document" />}
                titel={`${d.title}${d.type_name ? ` · ${d.type_name}` : ""}`}
                unten={[d.status, d.project_nr].filter(Boolean).join(" · ")}
                rechts={<>{euro(d.amount)}<br />{datum(d.date, false)}</>}
                href={heroLink(d.link_view) || undefined}
                extern={!!d.link_view}
              />
            ))}
          </Liste>
        )}

        {reiter === "appointments" && (
          <Liste leer="Keine Termine hinterlegt.">
            {nachDatum(daten.appointments).map((a) => (
              <Zeile
                key={a.id}
                icon={<KindIcon kind="appointment" />}
                titel={a.title}
                unten={a.is_done ? "erledigt" : a.all_day ? "ganztägig" : ""}
                rechts={datum(a.date, !a.all_day)}
                onClick={() => setTerminOffen(a)}
              />
            ))}
          </Liste>
        )}

        {reiter === "tasks" && (
          <Liste leer="Keine Aufgaben hinterlegt.">
            {nachDatum(daten.tasks).map((t) => (
              <Zeile
                key={t.id}
                icon={<KindIcon kind="task" />}
                titel={t.title}
                unten={[t.assignee, t.status, t.comment].filter(Boolean).join(" · ")}
                rechts={t.date ? datum(t.date, false) : ""}
                onClick={() => setAufgabeOffen(t)}
              />
            ))}
          </Liste>
        )}
      </div>

      {/* Deckel-Hinweis: sonst wirkt eine gekappte Liste vollstaendig. */}
      {(s.emails > (s.emails_shown ?? 0) || s.calls > (s.calls_shown ?? 0)) && (
        <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 8, textAlign: "right" }}>
          Angezeigt sind die letzten {s.limit} Einträge je Liste.
        </div>
      )}

      {/* ── Popups ──
          E-Mail bekommt eine eigene Leseansicht; Termin und Aufgabe nutzen die
          Modals, die es im Portal ohnehin schon gibt (CalendarEditModal,
          TaskEditModal) — dort kann man sie auch gleich bearbeiten. */}
      <EmailModal
        emailId={mailOffen}
        open={mailOffen != null}
        onClose={() => setMailOffen(null)}
      />
      <CalendarEditModal
        open={terminOffen != null}
        onClose={() => setTerminOffen(null)}
        tenant={tenant}
        event={terminOffen ? {
          id: String(terminOffen.id),
          title: terminOffen.title,
          start: terminOffen.date || "",
          end: terminOffen.end || "",
          allDay: terminOffen.all_day,
          // Ohne category_id zeigt das Modal den Platzhalter statt der
          // gesetzten Kategorie (gotcha_select_vorauswahl_async_optionen).
          category_id: terminOffen.category_id ?? null,
          project_match_id: terminOffen.project_id,
        } : null}
      />
      <TaskEditModal
        open={aufgabeOffen != null}
        onClose={() => setAufgabeOffen(null)}
        task={aufgabeOffen ? {
          // 🔴 Die LOKALE tasks.id, nicht `hero_task_id`: der Endpunkt
          // dahinter schlaegt per `get_task(task_id)` in der eigenen Tabelle
          // nach. Mit der HERO-ID haette er eine fremde Zeile bearbeitet.
          id: aufgabeOffen.id,
          hero_task_id: aufgabeOffen.hero_task_id,
          title: aufgabeOffen.title,
          comment: aufgabeOffen.comment,
          due_date: aufgabeOffen.date,
          tenant_slug: tenant,
          // 🔴 ID UND Name — das Modal waehlt den Mitarbeiter ueber `id|name`.
          // Ohne die ID stand dort „kein Mitarbeiter", obwohl einer zugewiesen
          // war (gemeldet 17.08.2026).
          target_user_id: aufgabeOffen.assignee_id,
          target_user_name: aufgabeOffen.assignee,
          project_match_id: aufgabeOffen.project_id,
          contact_id: k?.id ?? null,
        } : null}
      />
    </div>
  )
}
